import { useEffect, useState } from 'react'
import { Check, AlertTriangle, Stethoscope, Bus, PackagePlus } from 'lucide-react'
import { apiErrorText } from '../../lib/apiError.js'
import { ConfirmDialog, Checkbox, Segmented, fmtMoney } from '../../kit'
import {
  SHORTCUTS, FLUORIDE_OPTIONS, SEALANT_PRICE,
  shortcutItems, shortcutTotal, hasShortcutLines, runShortcut, undoShortcutLine,
} from '../../lib/billingShortcuts.js'

// Catalogue prices are display-only here (api 385 writes the money). Whole baht,
// Math.round first — the rounding fmtBill used, so a quoted price never differs
// from the line it creates.
const fmt = (n) => fmtMoney(n == null || n === '' ? n : Math.round(Number(n)))

/**
 * The billing shortcuts of ONE treatment card: their menu items, their dialogs
 * and the one bit of state they share. Returns { actions, status, dialog } for
 * TreatmentActionBar to render — the card stays a layout, not a controller.
 *
 * These are NOT top-level buttons: the bar folds every one of them behind the
 * caret of the เพิ่มรายการ Billing split button, so the default action keeps its
 * place however many presets get added.
 *
 * THREE INTERACTION MODES, and the reason each preset has the one it has:
 *
 *   instant  (fee)        One row, dedupe-guarded, and api 385 hands its id back
 *                         — so it can be written on the click and UNDONE. Fastest
 *                         path for the thing every visit needs.
 *   confirm  (ortho)      FOUR rows per click, no dedupe anywhere, and the
 *                         response names only the last one — there is no undo to
 *                         offer, so the dialog is the last chance to stop. It
 *                         lists what will be billed, priced from the catalogue.
 *   options  (toothtaxi)  The amounts are a clinical choice (฿100 vs ฿200
 *                         fluoride, sealant or not), so a click cannot mean one
 *                         thing. The picker IS the confirmation.
 *
 * ⚠️ EVERY preset re-runs autofeeBilling upstream, which can add, rewrite or
 * delete the visit's derived fee lines. `onChanged` must REFETCH the treatment,
 * never patch the returned row into the table in place.
 */
export function useTreatmentShortcuts({ treatmentId, lines, types, isToothTaxi, onChanged }) {
  const [busy, setBusy] = useState('')      // the running preset's key
  const [ask, setAsk] = useState('')        // the preset whose dialog is open
  const [tt, setTt] = useState({ fluoride: 200, sealant: false })
  const [done, setDone] = useState(null)    // { label, undoId }
  const [err, setErr] = useState('')

  // The ✓ / เลิกทำ chip is a receipt for the click, not a state — the new rows in
  // the table above it are the record. Ten seconds is long enough to notice a
  // mistake and short enough not to become furniture.
  useEffect(() => {
    if (!done && !err) return
    const h = setTimeout(() => { setDone(null); setErr('') }, 10000)
    return () => clearTimeout(h)
  }, [done, err])

  async function fire(key, extra) {
    if (busy) return
    setBusy(key); setErr(''); setDone(null); setAsk('')
    try {
      const { id } = await runShortcut(key, treatmentId, extra)
      setDone({ label: SHORTCUTS[key]?.label || '', undoId: id })
      onChanged?.()
    } catch (e) {
      setErr(apiErrorText(e))
    } finally {
      setBusy('')
    }
  }

  async function undo() {
    const id = done?.undoId
    if (!id || busy) return
    setBusy('undo'); setErr('')
    try {
      await undoShortcutLine(id)
      setDone(null)
      onChanged?.()
    } catch (e) {
      setErr(apiErrorText(e))
    } finally {
      setBusy('')
    }
  }

  const feeBilled = hasShortcutLines(lines, 'fee')
  const orthoTotal = shortcutTotal(types, 'ortho')

  const actions = [
    {
      key: 'fee',
      label: SHORTCUTS.fee.label,
      meta: fmtMoney(Math.round(Number(SHORTCUTS.fee.price)), { currency: 'prefix' }),
      icon: feeBilled ? Check : Stethoscope,
      onClick: () => fire('fee'),
      write: true,
      busy: busy === 'fee',
      disabled: feeBilled,
      hint: feeBilled
        ? 'การรักษานี้มีค่าบริการทางการแพทย์แล้ว'
        : `เพิ่มค่าบริการทางการแพทย์ ${fmt(SHORTCUTS.fee.price)} บาท ในคลิกเดียว`,
    },
    {
      key: 'ortho',
      label: SHORTCUTS.ortho.label,
      meta: orthoTotal ? fmtMoney(Math.round(Number(orthoTotal)), { currency: 'prefix' }) : '',
      icon: PackagePlus,
      onClick: () => setAsk('ortho'),
      write: true,
      busy: busy === 'ortho',
      hint: 'เพิ่มชุดจัดฟัน 4 รายการพร้อมกัน',
    },
    // Tooth Taxi bills สปสช. rates that only exist on a Tooth Taxi visit, so the
    // button is absent everywhere else rather than disabled — at a branch clinic
    // it is not a thing you were refused, it is not a thing that exists.
    isToothTaxi && {
      key: 'toothtaxi',
      label: SHORTCUTS.toothtaxi.label,
      meta: 'สปสช.',
      icon: Bus,
      onClick: () => { setTt({ fluoride: 200, sealant: false }); setAsk('toothtaxi') },
      write: true,
      busy: busy === 'toothtaxi',
      hint: 'เคลือบฟลูออไรด์ / เคลือบหลุมร่องฟัน — เบิก สปสช.',
    },
  ].filter(Boolean)

  const status = err ? (
    <span className="inline-flex items-center gap-1.5 text-[12.5px] text-rose-600">
      <AlertTriangle size={12} /> {err}
    </span>
  ) : done ? (
    <span className="inline-flex items-center gap-1.5 text-[12.5px] text-emerald-700">
      <Check size={12} /> เพิ่ม{done.label}แล้ว
      {done.undoId > 0 && (
        <button type="button" onClick={undo} disabled={!!busy}
          className="underline underline-offset-2 text-slate-500 hover:text-rose-600 disabled:opacity-40">เลิกทำ</button>
      )}
    </span>
  ) : null

  const dialog = ask === 'ortho' ? (
    <ConfirmDialog
      title="เพิ่มชุดจัดฟัน?"
      confirmLabel="เพิ่มทั้งชุด"
      message={<OrthoList types={types} total={orthoTotal} />}
      onConfirm={() => fire('ortho')}
      onCancel={() => setAsk('')}
    />
  ) : ask === 'toothtaxi' ? (
    <ConfirmDialog
      title="Tooth Taxi — เลือกรายการ"
      confirmLabel="เพิ่มรายการ"
      message={<ToothTaxiPicker value={tt} onChange={setTt} />}
      onConfirm={() => {
        if (!tt.fluoride && !tt.sealant) { setErr('เลือกอย่างน้อยหนึ่งรายการ'); setAsk(''); return }
        fire('toothtaxi', { tooth_taxi: { fluoride: tt.fluoride, sealant: tt.sealant } })
      }}
      onCancel={() => setAsk('')}
    />
  ) : null

  return { actions, status, dialog }
}

// Priced from the CATALOGUE, which is also where api 385 reads them — so the
// dialog and the write cannot quote different numbers. A row missing from the
// catalogue is simply not listed; the endpoint still bills it, which is why the
// total is labelled as the catalogue's and not as a promise.
function OrthoList({ types, total }) {
  const items = shortcutItems(types, 'ortho')
  if (!items.length) return <>เพิ่มรายการชุดจัดฟันทั้งหมดลงในการรักษานี้ — ยกเลิกได้ด้วยการลบรายการทีละบรรทัด</>
  return (
    <>
      <div className="rounded-xl ring-1 ring-slate-200 divide-y divide-slate-100 overflow-hidden">
        {items.map((t) => (
          <div key={t.id} className="flex items-center justify-between gap-3 px-3 py-1.5 text-[13.5px]">
            <span className="text-slate-700 truncate">{t.name || t.type}</span>
            <span className="tabular-nums text-slate-500 flex-none">{fmt(t.price)}</span>
          </div>
        ))}
        <div className="flex items-center justify-between gap-3 px-3 py-1.5 text-[13.5px] bg-slate-50">
          <span className="font-medium text-slate-600">รวม</span>
          <span className="tabular-nums font-semibold text-slate-800">{fmt(total)}</span>
        </div>
      </div>
      <div className="mt-2 text-[12.5px] text-slate-400">
        เพิ่มทีเดียว {items.length} รายการ — ถ้าเพิ่มผิดต้องลบทีละรายการในตาราง
      </div>
    </>
  )
}

// The สปสช. rates are fixed by the scheme, so these are choices between known
// amounts, never a free price field — the browser sends the CHOICE and api 385
// writes the money.
function ToothTaxiPicker({ value, onChange }) {
  return (
    <div className="space-y-3">
      <div>
        <div className="text-[12.5px] font-medium text-slate-600 mb-1">เคลือบฟลูออไรด์</div>
        {/* 0 = ไม่เพิ่ม. Segmented compares as strings and hands back the
            option's own (number) value, so `fluoride` stays 0 / 100 / 200. */}
        <Segmented value={value.fluoride || 0} onChange={(v) => onChange({ ...value, fluoride: v })}
          options={[{ value: 0, label: 'ไม่เพิ่ม' }, ...FLUORIDE_OPTIONS.map((p) => ({ value: p, label: `${fmt(p)} บาท` }))]} />
      </div>
      <Checkbox checked={value.sealant} onChange={(on) => onChange({ ...value, sealant: on })}
        label={`เคลือบหลุมร่องฟัน ${fmt(SEALANT_PRICE)} บาท`} />
      <div className="text-[12.5px] text-slate-400">
        ทั้งสองรายการเบิก สปสช. — รายการที่คนไข้มีอยู่แล้วในวันนี้จะไม่ถูกเพิ่มซ้ำ
      </div>
    </div>
  )
}
